<template>
  <section class="GiftTiers pb-7 mb-9">
    <header class="flex items-center justify-start gap-x-3 mt-6 mx-5 md:ml-0">
      <svg-icon-cart-gift class="w-6 h-6" />
      <h3 class="font-body font-semibold text-lg">{{ $t('giftTiersTitle') }}</h3>
    </header>
    <div class="flex items-center justify-between font-body text-base uppercase mt-4 mb-3 mx-5 md:ml-0">
      <span class="text-primary-1-60">{{ amountRemaining }} {{ $t('egp') }} {{ $t('remainingAmount') }}</span>
      <span class="text-primary-1-100">{{
        progressDone === 100 ? $t('enjoyYourGift') : `${nextLevelAmount} ${$t('egp')}`
      }}</span>
    </div>
    <div class="ProgressBar mx-5 md:ml-0">
      <div class="bg-system-green absolute top-0 left-0 bottom-0" :style="`width: ${progressDone}%`"></div>
    </div>
    <div class="flex items-center justify-between font-body text-base mt-3 mb-4 mx-5 md:ml-0">
      <span v-if="currentTier !== 0" class="text-primary-1-60">{{ $t('tierNo', [currentTier]) }}</span>
      <span class="text-primary-1-100" :class="{ 'ml-auto': currentTier === 0 }">{{
        $t('tierNo', [Math.min(currentTier + 1, Number(cartPromotions.options?.length))])
      }}</span>
    </div>
    <p v-if="currentTier" class="mt-6 text-base text-primary-1-60 font-body mx-5 md:ml-0">
      {{
        progressDone === 100
          ? $t('enjoyYourGiftBody', [lastTierGiftName])
          : $t('giftTiersMessage', [currentTier, amountRemaining])
      }}
    </p>
    <p v-else class="mt-6 text-base text-primary-1-60 font-body mx-5 md:ml-0">
      {{ $t('noTierMessage', [amountRemaining]) }}
    </p>
    <div class="Gifts mt-6 flex gap-x-3 md:gap-x-6 overflow-x-auto px-5 md:px-0">
      <article
        v-for="(option, idx) in cartPromotions.options"
        :key="idx"
        class="Gift relative flex flex-col flex-shrink-0 transition-opacity duration-700"
        :class="{ 'opacity-50': !option?.is_eligible }"
      >
        <span
          v-if="!Number(option?.gift?.stock)"
          class="[ absolute left-1/2 top-0 transform z-10 ] [ text-sm text-white text-center whitespace-nowrap ] [ rounded-b-sm bg-red-700 px-2 py-1 ]"
          :class="{ '-translate-x-1/2': $i18n.locale === 'en', 'translate-x-1/2': $i18n.locale === 'ar' }"
          >{{ $t('tierSoldOut') }}</span
        >
        <img
          class="Gift__img w-full rounded-md mb-3"
          :src="option?.gift?.thumbnail"
          :alt="option?.gift?.name"
          :class="{ 'opacity-50': !option?.gift?.stock }"
        />
        <p class="text-base mb-2">
          {{ option?.gift?.name }}
        </p>
        <p class="text-base font-bold mt-auto flex items-center gap-x-1">
          <transition name="fade">
            <svg-icon-gift-lock
              v-if="!option?.is_eligible"
              width="20"
              height="20"
              viewBox="0 0 26 26"
              class="text-primary-1-100"
            />
          </transition>
          {{ $t('tierNo', [idx + 1]) }}
        </p>
      </article>
    </div>
  </section>
</template>

<script setup lang="ts">
import { useGiftTiers, useNextLevelAmount } from '~/utils/gift-tiers';

const { t: $t } = useI18n({
  useScope: 'local',
});

const { subtotal } = useCartAttributes();
const { cartPromotions } = useExtendedCartAttributes();

const lastTierGiftName = computed(() => {
  const lastTier = cartPromotions.value?.options?.[cartPromotions.value?.options?.length - 1];

  if (Array.isArray(lastTier?.gift)) {
    return lastTier?.gift[0]?.name;
  }

  return lastTier?.gift?.name;
});

const { nextLevelAmount } = useNextLevelAmount(cartPromotions);

const { amountRemaining, currentTier, progressDone } = useGiftTiers(
  nextLevelAmount,
  subtotal,
  computed(() => ({
    tiers:
      cartPromotions?.value?.options?.map(option => ({
        discountValue: option?.tier_minimum_subtotal || 0,
      })) || [],
    eligibleTierIdx: cartPromotions?.value.eligiblePromotionIdx,
  })),
);
</script>

<style lang="postcss" scoped>
.ProgressBar {
  @apply relative bg-system-gray rounded-md;
  height: 2px;
}

.GiftTiers {
  @apply relative;

  &::before {
    @apply bg-deals-45;
    content: '';
    position: absolute;
    top: 100%;
    left: 1.25rem;
    right: 1.25rem;
    height: 1px;

    @screen md {
      left: 0;
      right: 0;
    }
  }
}

.Gifts {
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none; /* Safari and Chrome */
  }

  .Gift {
    max-width: 152px;
    &__img {
      background: linear-gradient(360deg, #ebebeb 0%, #f0f0f0 100%);
      box-shadow: 0px 0px 48px 0px rgba(0, 0, 0, 0.04);
    }
  }
}
</style>

<i18n>
  {
    "en": {
      "giftTiersTitle": "Free Gifts With Each Order",
      "remainingAmount": "TO GO",
      "noTierMessage": "Hurry! You've got {0} EGP left to spend on your favorite product and unlock amazing free gifts!",
      "giftTiersMessage": "Congrats! You unlocked the gift for tier {0} & you're {1} EGP away from unlocking the next tier gift!",
      "enjoyYourGift": "Enjoy your gift",
      "enjoyYourGiftBody": "Congrats, you won a {0}! Enjoy shopping with us.",
      "tierNo": "Tier {0}",
      "tierSoldOut": "Tier Sold Out"
    },
    "ar": {
      "giftTiersTitle": "هدايا مجانية مع كل طلب",
      "remainingAmount": "متبقي",
      "noTierMessage": "أسرع! لديك {0} جم متبقي لتنفقها على منتجك المفضل وتحصل على هدايا مجانية رائعة!",
      "giftTiersMessage": "مبروك! لقد فتحت الهدية للمستوى {0} وأنت على بعد {1} جم من فتح الهدية للمستوى التالي!",
      "enjoyYourGift": "استمتع بالهدية",
      "enjoyYourGiftBody": "مبروك، لقد فزت ب{0}! استمتع بالتسوق معنا.",
      "tierNo": "المستوى {0}",
      "tierSoldOut": "لقد نفذ المستوى"
    }
  }
  </i18n>
